import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, Search, MapPin, Phone, FileText, Download } from "lucide-react";
import api, { formatError, API } from "@/lib/api";
import logoWhite from "@/assets/logo-white.png";
import { fmtDate } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AcceptanceGate } from "./AcceptanceGate";

export default function PublicTracking() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [repairNumber, setRepairNumber] = useState("");
  const [last4, setLast4] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadToken = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/public/tracking/${token}`); setData(data); }
    catch (e) { setError(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) loadToken(); }, [token, loadToken]);

  useEffect(() => {
    if (!data) return;
    const t = setInterval(() => {
      const url = data.public_token ? `/public/tracking/${data.public_token}` : null;
      if (url) api.get(url).then((r) => setData(r.data)).catch(() => {});
    }, 12000);
    return () => clearInterval(t);
  }, [data]);

  const search = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/public/tracking", { repair_number: repairNumber, phone_last4: last4 });
      setData(data);
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white grain-panel">
      <header className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-6">
        <img src={logoWhite} alt="Dexter Lab" className="h-8 w-auto object-contain" />
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Tracking riparazione</div>
      </header>

      <main className="mx-auto max-w-xl px-6 pb-20">
        {!data && (
          <form onSubmit={search} className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur">
            <h1 className="font-heading text-3xl font-extrabold">Segui la tua riparazione</h1>
            <p className="mt-2 text-sm text-slate-400">Inserisci il numero pratica e le ultime 4 cifre del tuo telefono.</p>
            {error && <div className="mt-4 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</div>}
            <div className="mt-6 space-y-4">
              <div><label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">Numero pratica</label>
                <Input data-testid="tracking-number" value={repairNumber} onChange={(e) => setRepairNumber(e.target.value)}
                  placeholder="DL-2026-0001" className="border-white/10 bg-white/5 text-white placeholder:text-slate-500" /></div>
              <div><label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">Ultime 4 cifre telefono</label>
                <Input data-testid="tracking-last4" value={last4} onChange={(e) => setLast4(e.target.value)} maxLength={4}
                  placeholder="4567" className="border-white/10 bg-white/5 text-white placeholder:text-slate-500" /></div>
            </div>
            <Button type="submit" data-testid="tracking-search" disabled={loading} className="mt-6 w-full">
              <Search className="mr-2 h-4 w-4" /> Cerca
            </Button>
          </form>
        )}

        {data && token && data.acceptance && !data.terms_accepted_at && <AcceptanceGate data={data} onAccepted={setData} />}
        {data && (
          <div className="mt-8 space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur" data-testid="tracking-result">
              <div className="font-mono text-sm text-primary">{data.repair_number}</div>
              <h1 className="mt-1 font-heading text-3xl font-extrabold">{data.device.brand} {data.device.model}</h1>
              <div className="mt-3 inline-flex items-center rounded-full bg-primary/20 px-4 py-1.5 text-sm font-semibold text-primary">
                {data.status.label}
              </div>

              <div className="mt-8 space-y-0">
                {data.steps.map((s, i) => (
                  <div key={s.code} className="flex gap-3 pb-5 last:pb-0">
                    <div className="flex flex-col items-center">
                      {s.done ? <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                        : s.current ? <div className="h-6 w-6 rounded-full border-2 border-primary bg-primary/30" />
                        : <Circle className="h-6 w-6 text-slate-600" />}
                      {i < data.steps.length - 1 && <div className={`w-0.5 flex-1 ${s.done ? "bg-emerald-400/40" : "bg-slate-700"}`} />}
                    </div>
                    <div className={`pt-0.5 ${s.done || s.current ? "text-white" : "text-slate-500"}`}>
                      <div className="text-sm font-medium">{s.label}</div>
                      {s.current && <div className="text-xs text-primary">In corso</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-sm backdrop-blur">
              <div className="mb-2 text-xs uppercase tracking-wider text-slate-400">Punto vendita</div>
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {data.store.name}</div>
              {data.store.phone && <div className="mt-1 flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {data.store.phone}</div>}
              <div className="mt-3 text-xs text-slate-500">Ultimo aggiornamento: {fmtDate(data.updated_at)}</div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
              <div className="mb-3 text-xs uppercase tracking-wider text-slate-400">Documenti</div>
              <div className="space-y-2">
                <Button variant="outline" data-testid="download-receipt-btn"
                  className="w-full justify-start border-white/20 bg-transparent text-white hover:bg-white/10"
                  onClick={() => window.open(`${API}/public/receipt/${data.public_token}?type=receipt`, "_blank")}>
                  <Download className="mr-2 h-4 w-4" /> Scarica scheda riparazione (PDF)
                </Button>
                <Button variant="outline" data-testid="download-delivery-btn"
                  className="w-full justify-start border-white/20 bg-transparent text-white hover:bg-white/10"
                  onClick={() => window.open(`${API}/public/receipt/${data.public_token}?type=delivery`, "_blank")}>
                  <Download className="mr-2 h-4 w-4" /> Scarica modulo di accettazione (PDF)
                </Button>
              </div>
            </div>

            <Button variant="outline" data-testid="view-quote-btn"
              className="w-full border-white/20 bg-transparent text-white hover:bg-white/10"
              onClick={() => navigate(`/preventivo/${data.public_token}`)}>
              <FileText className="mr-2 h-4 w-4" /> Visualizza preventivo
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
